// /quiz/result?x=...&y=...&matches=...
//
// Standalone page that re-renders a sharable quiz result from URL
// params alone. Two consumers:
//
//   1. Human visitors who follow a shared link — see a polished
//      "I took the quiz" page with the user's compass position and
//      top-3 MP matches, plus a CTA to take the quiz themselves.
//   2. Open Graph crawlers (Facebook / Twitter / Slack / etc.) — read
//      the <meta og:image> tag pointing at /api/og/quiz-result with
//      the same params. The image is the same data rendered by
//      @vercel/og as a 1200×630 PNG.
//
// Stateless: no DB lookup against a per-result row, no shortened
// URL service. The encoded URL IS the result. Privacy: only compass
// position + 3 candidate ids leak, not raw answers.

import { useEffect, useMemo, useState } from 'react';
// Site chrome (Header/Footer) only on the error/loading branches —
// the success path is intentionally a poster-style standalone view
// that mirrors the share image's running order.
import Header from '../Header';
import Footer from '../Footer';
import { decodeShareState, encodeShareState, isShareEnabled, SHARE_ORIGIN } from '../../lib/quizShareUrl';
import type { CandidateRow } from '../../db/types';

interface QuizApiResponse {
  candidates: CandidateRow[];
}

// PARTY_COLOURS was used by the retired ResultCard — RankCardLite
// uses the rank's accent colour for the left strip + score chip
// instead of the candidate's party colour, so the table is no
// longer referenced.

export default function QuizResultPage() {
  const params =
    typeof window === 'undefined'
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search);
  const state = decodeShareState(params);
  const enabled = isShareEnabled();

  // Set OG meta tags on mount so crawlers that execute JS get them too.
  // (Most crawlers don't, so we also set them in index.html — but we
  // don't have a per-result rendered HTML, so this is a fallback. The
  // important crawler-readable tags are inserted via vercel.json
  // headers if needed; for now just doc-side.)
  useEffect(() => {
    if (!state || typeof document === 'undefined') return;
    const encoded = encodeShareState(state);
    const canonical = `${SHARE_ORIGIN}/quiz/result?${encoded}`;
    const ogImage = `${SHARE_ORIGIN}/api/og/quiz-result?${encoded}&size=fb`;
    setMeta('og:title', 'Where I land on the political compass — Lovin Malta Election 2026');
    setMeta('og:description', 'My closest match and the candidate furthest from my views.');
    setMeta('og:image', ogImage);
    setMeta('og:url', canonical);
    setMeta('og:type', 'website');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:image', ogImage);
    document.title = 'My political compass · Lovin Malta';
  }, [state]);

  if (!enabled) {
    return (
      <>
        <Header />
        <main style={containerStyle}>
          <h1 style={titleStyle}>Result share is preview-only</h1>
          <p style={ledeStyle}>
            This feature is currently being tested on the develop preview
            deploy. Check back after launch.
          </p>
        </main>
        <Footer />
      </>
    );
  }

  if (!state) {
    return (
      <>
        <Header />
        <main style={containerStyle}>
          <h1 style={titleStyle}>No result to show</h1>
          <p style={ledeStyle}>
            Looks like the link is missing parameters. Take the quiz to
            generate your own result.
          </p>
          <a href="/#quiz" style={ctaStyle}>
            Go to the quiz →
          </a>
        </main>
        <Footer />
      </>
    );
  }

  return <ResultBody state={state} />;
}

function ResultBody({ state }: { state: NonNullable<ReturnType<typeof decodeShareState>> }) {
  const [candidates, setCandidates] = useState<CandidateRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch('/api/quiz', { credentials: 'include' })
      .then((r) => r.json())
      .then((data: QuizApiResponse) => {
        if (cancelled) return;
        setCandidates(data.candidates ?? []);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const matches = useMemo(() => {
    if (!candidates) return null;
    const byId = new Map(candidates.map((c) => [c.id, c] as const));
    const closest = byId.get(state.topId);
    const second = state.top2Id != null ? byId.get(state.top2Id) : undefined;
    const third = state.top3Id != null ? byId.get(state.top3Id) : undefined;
    const furthest = state.bottomId != null ? byId.get(state.bottomId) : undefined;
    return { closest, second, third, furthest };
  }, [
    candidates,
    state.topId,
    state.top2Id,
    state.top3Id,
    state.bottomId,
  ]);

  // The URL contains the voter's projection — no MP-driven rescaling
  // any more, so just clamp and render at the encoded coordinate.
  const userX = Math.max(-1, Math.min(1, state.x));
  const userY = Math.max(-1, Math.min(1, state.y));

  return (
    <main style={containerStyle}>
      <LogoHeader />

      {error && <p style={errorStyle}>Couldn't load matches: {error}</p>}
      {!candidates && !error && (
        <p style={{ fontSize: 16, color: '#888', textAlign: 'center' }}>Loading…</p>
      )}

      {matches && (
        <div style={rankingRowStyle}>
          {/* Most similar — top 3, decreasing redness. */}
          <div style={columnStyle}>
            <h2 style={{ ...columnHeadingStyle, color: '#FF1349', borderColor: '#FF1349' }}>
              Most similar
            </h2>
            {matches.closest && (
              <RankCardLite
                candidate={matches.closest}
                rank={1}
                accent="#FF1349"
                score={state.topScore}
              />
            )}
            {matches.second && (
              <RankCardLite
                candidate={matches.second}
                rank={2}
                accent="#FF6585"
                score={state.top2Score}
              />
            )}
            {matches.third && (
              <RankCardLite
                candidate={matches.third}
                rank={3}
                accent="#FFA0B5"
                score={state.top3Score}
              />
            )}
          </div>

          {/* Least similar — single black card. */}
          <div style={columnStyle}>
            <h2 style={{ ...columnHeadingStyle, color: '#0E0E0E', borderColor: '#0E0E0E' }}>
              Least similar
            </h2>
            {matches.furthest && (
              <RankCardLite
                candidate={matches.furthest}
                rank={null}
                accent="#0E0E0E"
                score={state.bottomScore}
                inverted
              />
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40, marginBottom: 32 }}>
        <CompassLarge x={userX} y={userY} />
      </div>

      <ShareButtons />
    </main>
  );
}

// Compact card used by both columns on the standalone summary page —
// keeps the page's layout in sync with the OG image so the result
// recipients see the same composition whether they're on the page or
// looking at the shared post.
function RankCardLite({
  candidate,
  rank,
  accent,
  score,
  inverted,
}: {
  candidate: CandidateRow;
  rank: number | null;
  accent: string;
  score: number | null;
  inverted?: boolean;
}) {
  return (
    <article
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: inverted ? '#1A1A1A' : '#fff',
        border: inverted ? 'none' : `1px solid #e5e5e5`,
        borderLeft: inverted ? 'none' : `8px solid ${accent}`,
        borderRadius: 8,
        padding: '12px 14px',
        boxShadow: inverted
          ? '0 4px 14px rgba(0,0,0,0.18)'
          : `0 2px 6px ${accent}15`,
      }}
    >
      {rank != null && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            background: accent,
            color: '#fff',
            borderRadius: 16,
            fontSize: 14,
            fontWeight: 900,
            flexShrink: 0,
          }}
        >
          {rank}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 16,
            fontWeight: 800,
            color: inverted ? '#fff' : '#1a1a1a',
            lineHeight: 1.15,
          }}
        >
          {candidate.full_name}
        </div>
        <div
          style={{
            fontSize: 12,
            color: inverted ? '#bbb' : '#666',
            marginTop: 2,
          }}
        >
          {candidate.party}
          {score != null && (
            <span style={{ marginLeft: 8, fontWeight: 800, color: inverted ? '#fff' : accent }}>
              {score}%
            </span>
          )}
        </div>
      </div>
    </article>
  );
}

const rankingRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: 18,
  marginBottom: 8,
  alignItems: 'flex-start',
  flexWrap: 'wrap',
};
const columnStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 240px',
  gap: 10,
};
const columnHeadingStyle: React.CSSProperties = {
  margin: '0 0 10px',
  paddingBottom: 6,
  borderBottom: '3px solid',
  fontSize: 13,
  fontWeight: 900,
  letterSpacing: 3,
  textTransform: 'uppercase',
};

// Standalone brand mark for the result page — the page deliberately
// drops the site Header so the layout mirrors the OG share image.
function LogoHeader() {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        marginBottom: 28,
        paddingTop: 8,
      }}
    >
      <img
        src="/assets/lovin-malta-logo.png"
        alt="Lovin Malta"
        style={{ display: 'block', height: 64, width: 'auto' }}
      />
      <p
        style={{
          margin: '12px 0 0',
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: 4,
          textTransform: 'uppercase',
          color: '#888',
        }}
      >
        Election 2026
      </p>
    </div>
  );
}

// ResultCard / matchListStyle retired with the top-3 + bottom column
// layout — RankCardLite + columnStyle replace them. matchCardStyle /
// matchNameStyle / matchMetaStyle remain referenced by nothing now;
// keeping them around won't hurt (no runtime cost; trivially dead) so
// stripping them is left as a follow-up if anyone wants the cleanup.

// ─── Compass component (matches the OG image styling) ─────────────

function CompassLarge({ x, y }: { x: number; y: number }) {
  const PX = 320;
  const cx = ((x + 1) / 2) * PX;
  const cy = ((1 - y) / 2) * PX;
  const labelStyle: React.CSSProperties = {
    position: 'absolute',
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 1.4,
    textTransform: 'uppercase',
    color: '#777',
    pointerEvents: 'none',
  };
  return (
    <div
      style={{
        position: 'relative',
        width: PX,
        height: PX,
        background: '#fff',
        border: '2px solid #d8d2c4',
        borderRadius: 14,
        flexShrink: 0,
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      }}
    >
      <div style={{ position: 'absolute', left: 0, top: PX / 2 - 1, width: PX, height: 2, background: '#ece6d6' }} />
      <div style={{ position: 'absolute', top: 0, left: PX / 2 - 1, width: 2, height: PX, background: '#ece6d6' }} />
      <div style={{ ...labelStyle, top: 8, left: 0, width: PX, textAlign: 'center' }}>
        Collective
      </div>
      <div style={{ ...labelStyle, bottom: 8, left: 0, width: PX, textAlign: 'center' }}>
        Self-reliance
      </div>
      <div
        style={{
          ...labelStyle,
          left: 8,
          top: '50%',
          transform: 'translateY(-50%) rotate(-90deg)',
          transformOrigin: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        Stewardship
      </div>
      <div
        style={{
          ...labelStyle,
          right: 8,
          top: '50%',
          transform: 'translateY(-50%) rotate(90deg)',
          transformOrigin: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        Transformation
      </div>
      <div
        style={{
          position: 'absolute',
          left: cx - 14,
          top: cy - 14,
          width: 28,
          height: 28,
          background: '#e11d2a',
          borderRadius: 14,
          boxShadow: '0 0 0 6px rgba(225,29,42,0.18), 0 0 0 12px rgba(225,29,42,0.08)',
          color: '#fff',
          fontSize: 10,
          fontWeight: 900,
          letterSpacing: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '2px solid #fff',
        }}
      >
        ME
      </div>
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────

function setMeta(prop: string, content: string): void {
  const isOg = prop.startsWith('og:') || prop.startsWith('twitter:');
  const selector = isOg
    ? `meta[property="${prop}"], meta[name="${prop}"]`
    : `meta[name="${prop}"]`;
  let tag = document.querySelector<HTMLMetaElement>(selector);
  if (!tag) {
    tag = document.createElement('meta');
    if (prop.startsWith('og:')) tag.setAttribute('property', prop);
    else tag.setAttribute('name', prop);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

// ─── Share button ──────────────────────────────────────────────────
//
// Single universal share trigger. Tries three paths in order:
//   1. Native share sheet with the OG image as a FILE (mobile only).
//      Lets Instagram / Facebook open the Story / Reel / Post
//      composer with the image pre-loaded — instead of sharing a
//      link via DM, which is what we had before.
//   2. Native share sheet with just the URL (mobile without file
//      support).
//   3. Copy-to-clipboard (desktop).

function ShareButtons() {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  // Use the current origin so a share from develop preview points at
  // develop, and a share from prod points at prod. SHARE_ORIGIN is
  // still used by the og:image meta tag where a canonical absolute
  // URL is required for crawler consistency.
  const origin = typeof window !== 'undefined' ? window.location.origin : SHARE_ORIGIN;
  const params =
    typeof window !== 'undefined' ? new URL(window.location.href).search : '';
  const pageUrl = `${origin}/quiz/result${params}`;
  const imageUrl = `${origin}/api/og/quiz-result${params}${params ? '&' : '?'}size=ig`;

  async function tryShareImage(): Promise<boolean> {
    if (typeof navigator.canShare !== 'function' || typeof navigator.share !== 'function') {
      return false;
    }
    try {
      const res = await fetch(imageUrl);
      if (!res.ok) return false;
      const blob = await res.blob();
      const file = new File([blob], 'lovin-malta-quiz.png', { type: 'image/png' });
      if (!navigator.canShare({ files: [file] })) return false;
      await navigator.share({
        files: [file],
        title: 'My Lovin Malta election quiz result',
        text: 'See where I land on the political compass.',
      });
      return true;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return true;
      return false;
    }
  }

  async function tryShareUrl(): Promise<boolean> {
    if (typeof navigator.share !== 'function') return false;
    try {
      await navigator.share({
        title: 'My Lovin Malta election quiz result',
        text: 'See where I land on the political compass.',
        url: pageUrl,
      });
      return true;
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return true;
      return false;
    }
  }

  async function handleShare() {
    if (busy) return;
    setBusy(true);
    try {
      if (await tryShareImage()) return;
      if (await tryShareUrl()) return;
      try {
        await navigator.clipboard.writeText(pageUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        /* ignore */
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={shareSectionStyle}>
      <button
        type="button"
        onClick={() => void handleShare()}
        disabled={busy}
        title={copied ? 'Link copied ✓' : busy ? 'Preparing…' : 'Share my result'}
        aria-label={copied ? 'Link copied' : 'Share my result'}
        style={{
          background: '#FF1349',
          color: '#fff',
          border: 'none',
          borderRadius: 999,
          padding: '14px 28px',
          fontSize: 15,
          fontWeight: 800,
          letterSpacing: 0.4,
          cursor: busy ? 'wait' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          boxShadow: '0 6px 18px rgba(255,19,73,0.32), 0 1px 3px rgba(0,0,0,0.12)',
          opacity: busy ? 0.7 : 1,
          fontFamily: 'inherit',
        }}
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          {copied ? (
            <polyline points="20 6 9 17 4 12" />
          ) : (
            <>
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
              <polyline points="16 6 12 2 8 6" />
              <line x1="12" y1="2" x2="12" y2="15" />
            </>
          )}
        </svg>
        {copied ? 'Link copied' : busy ? 'Preparing…' : 'Share my result'}
      </button>
    </div>
  );
}

const shareSectionStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  margin: '12px 0 24px',
};

// ─── Styles (inline to keep this component self-contained) ─────────

const containerStyle: React.CSSProperties = {
  maxWidth: 800,
  margin: '0 auto',
  padding: '40px 24px 60px',
  fontFamily: 'var(--font-body, sans-serif)',
};
// titleStyle / ledeStyle remain — still used by the error/loading
// branches' h1 + paragraph. kickerStyle + layoutStyle (row-flex
// wrapper) were retired with the standalone redesign.
const titleStyle: React.CSSProperties = {
  margin: '8px 0 4px',
  fontSize: 56,
  fontWeight: 900,
  color: '#1a1a1a',
  letterSpacing: -1.5,
  lineHeight: 1.05,
};
const ledeStyle: React.CSSProperties = {
  margin: '0 0 32px',
  fontSize: 22,
  color: '#555',
};
// matchListStyle / matchCardStyle / matchNameStyle / matchMetaStyle
// retired with the column rankings; styles are inlined in
// RankCardLite + rankingRowStyle / columnStyle / columnHeadingStyle
// (defined alongside RankCardLite above).
const ctaStyle: React.CSSProperties = {
  display: 'inline-block',
  background: '#e11d2a',
  color: '#fff',
  padding: '14px 24px',
  borderRadius: 6,
  fontWeight: 800,
  textDecoration: 'none',
  fontSize: 16,
  letterSpacing: 0.5,
};
const errorStyle: React.CSSProperties = {
  background: '#fff5f7',
  border: '1px solid #f5c6c6',
  borderRadius: 4,
  padding: '10px 12px',
  color: '#b00020',
  fontSize: 14,
};
